<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import head from 'lodash-es/head';
import Permissions from '../../../../shared/permissions';
import FileManager from '../../filemanager/FileManager.vue';

const { $t } = useLocalization();
const { $permissions } = usePermissions();
const { $events } = useEvents();

const props = withDefaults(defineProps<{
    modelValue?: any;
    media?: string;
    maxFiles?: number;
    name?: string;
    disabled?: boolean;
}>(), {
    modelValue: null,
    media: 'files',
    maxFiles: 1,
    name: ''
});

const emit = defineEmits<{
    (e: 'update:modelValue', files: any[]): void;
}>();

const filePicker = ref(null);

const modal = ref(false);
const allowed = ref(false);

const model = computed((): any[] =>
{
    if (props.modelValue === null)
        return [];

    if (props.modelValue.hasOwnProperty('length'))
        return props.modelValue as any[];

    return [props.modelValue as any];
});

const title = computed((): string =>
{
    return props.maxFiles === 1
        ? $t('[[[Wybierz plik]]]')
        : props.maxFiles === 0
            ? $t('[[[Wybierz pliki]]]')
            : $t('[[[Wybierz pliki (do {0})|||%0]]]', props.maxFiles);
});

const label = computed((): string =>
{
    return model.value.length > 0 ? $t('[[[Wybrane pliki: {0}|||%0]]]', model.value.length) : title.value;
});

const openModal = (): void =>
{
    modal.value = true;
};

const closeModal = (): void =>
{
    modal.value = false;
};

const returnModel = (files: any[]): any[] | any =>
{
    filePicker.value.querySelector('input').dispatchEvent(new Event('input', { bubbles: true }));

    if (props.maxFiles === 0)
        emit('update:modelValue', files);
    else if (props.maxFiles === 1)
        emit('update:modelValue', head(files));
    else
        emit('update:modelValue', files.slice(0, props.maxFiles));
};

const remove = (file: any): void =>
{
    if (!props.disabled)
        returnModel(model.value.filter(p => p !== file));
};

const filesSelected = (files: any[]): void =>
{
    if (modal.value === true)
    {
        returnModel(files);
        closeModal();
    }
};

const clear = (): void =>
{
    emit('update:modelValue', props.maxFiles === 1 ? null : []);
};

onMounted(async (): Promise<void> =>
{
    allowed.value = await $permissions.all([Permissions.Storage.Access]);
    $events.$on('filemanager::modal::close', closeModal);
    $events.$on('filemanager::callback::value', filesSelected);
});

onUnmounted(async (): Promise<void> =>
{
    $events.$off('filemanager::modal::close', closeModal);
    $events.$off('filemanager::callback::value', filesSelected);
});
</script>

<template>
    <div ref="filePicker">
        <div v-if="model.length > 0" class="rounded-top p-3 pb-0 mb-0 bg-dark">
            <div class="d-flex flex-wrap justify-content-around">
                <div v-for="(file, index) in model" :key="`${model.length}.${index}`" class="item mb-3 me-1 ms-1" :class="{ 'small': model.length != 1, 'disabled': disabled }">
                    <IdeoImg v-if="file.isImage" thumbnail :src="$filters.image(file.url, 'thumb')" :alt="file.name" />
                    <div v-else class="img-thumbnail h-100" :title="file.name">
                        <div class="h-100 d-flex align-items-center text-center">
                            <span>{{ file.name }}</span>
                        </div>
                    </div>
                    <i class="remove far fa-times-circle" @click.stop="remove(file)" />
                </div>
            </div>
        </div>
        <IdeoInputGroup :class="{ 'active': model.length > 0 }">
            <IdeoFormInput
                :placeholder="title"
                :value="label"
                :class="{ 'bg-dark border-dark text-muted': model.length > 0 }"
                :name="name"
                :disabled="disabled"
                readonly
            />
            <template #append>
                <IdeoButton v-if="model.length > 0" variant="danger" :title="$t('[[[Usuń plik]]]')" :disabled="!allowed || disabled" @click="clear()">
                    <i class="fas fa-fw fa-times" />
                </IdeoButton>
                <IdeoButton variant="primary" :title="title" :disabled="!allowed || disabled" @click="openModal()">
                    <i class="fas fa-fw fa-folder-open" />
                </IdeoButton>
            </template>
        </IdeoInputGroup>
        <div v-if="modal" class="app-modal filepicker d-flex flex-column">
            <FileManager :value="model" :modal="true" :media="media" :max-files="maxFiles" />
        </div>
    </div>
</template>

<style lang="scss" scoped>
.item {
    position: relative;

    &.disabled {
        opacity: 0.65;
    }

    &.small .img-thumbnail {
        max-width: 100px;
        min-height: 100px;
    }

    .img-thumbnail {
        max-width: 150px;
        min-height: 150px;

        > div {
            overflow: hidden;
        }
    }

    .remove {
        position: absolute;
        right: -4px;
        top: -4px;
        background-color: var(--bs-white);
        color: var(--bs-red);
        border-radius: 10px;
        font-size: 20px;
        z-index: 3;
    }
}

input {
    &::placeholder {
        color: var(--bs-body);
    }
}

.input-group {
    button {
        margin-left: 0 !important;
    }

    &.active {
        & > :first-child {
            border-top-left-radius: 0;
            border: none;
        }

        & > :last-child {
            border-top-right-radius: 0;
        }
    }
}
</style>
